import { useEffect, useRef, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { actionLinkLifetime } from "@rago/web-shared";
import { z } from "zod";
import { auth } from "../../lib/authManager";
import { useAuth } from "../../lib/useAuth";
import { confirmEmailChange, describeAccountError, isLinkVerdict } from "../../lib/accountActions";
import { ErrorNote, SuccessNote, Spinner } from "../../components/ui";
import { AuthBody, AuthHeading, AuthPending } from "../../components/auth-card";

export const Route = createFileRoute("/auth/confirm-email")({
  // The router JSON-parses search values: a token that isn't a string reads
  // as missing rather than failing the page.
  validateSearch: z.object({ token: z.string().optional().catch(undefined) }),
  component: ConfirmEmail,
  pendingComponent: AuthPending,
});

/** The same number the API stamps on the token and prints in the mail. */
const LINK_LIFE = actionLinkLifetime("email_change");

/**
 * Where the link mailed to a NEW address lands (Settings › Account › Change
 * email): opening it is what changes the account's email. The token is the
 * proof, so the page works signed out — the mail is often opened on another
 * device than the one that asked.
 */
function ConfirmEmail() {
  const { token } = Route.useSearch();
  const session = useAuth();
  const [state, setState] = useState<"working" | "done" | "failed" | "missing">(
    token ? "working" : "missing",
  );
  const [error, setError] = useState<unknown>(null);

  function confirm(linkToken: string) {
    setState("working");
    setError(null);
    confirmEmailChange(linkToken)
      .then(() => {
        setState("done");
        // The signed-in profile shows the new address, verified, and no
        // change waiting. Not awaited: nothing here reads the answer.
        if (auth.getState().status === "signedIn") void auth.reloadMe();
      })
      .catch((err: unknown) => {
        setError(err);
        setState("failed");
      });
  }

  // StrictMode runs the effect twice in development; the link is used once.
  // (A repeat would be answered with the same success — a second tab
  // opening the mail's link is fine.)
  const fired = useRef(false);
  useEffect(() => {
    if (!token || fired.current) return;
    fired.current = true;
    confirm(token);
  }, [token]);

  // A dead link's way out is a new change, which takes a session: a
  // signed-out reader goes through sign-in and lands on Account.
  function startAgain() {
    if (session.status === "loading") return <Spinner />;
    if (session.status === "signedIn") {
      return (
        <>
          <p className="mb-4 text-sm text-[var(--rago-text-2)]">
            Confirmation links work for {LINK_LIFE}. Start the change again in Settings.
          </p>
          <Link to="/settings/account" className="rago-btn rago-btn-block rago-btn-lg">
            Open Account settings
          </Link>
        </>
      );
    }
    return (
      <>
        <p className="mb-4 text-sm text-[var(--rago-text-2)]">
          Confirmation links work for {LINK_LIFE}. Sign in and start the change again.
        </p>
        <Link
          to="/auth/signin"
          search={{ next: "/settings/account" }}
          className="rago-btn rago-btn-block rago-btn-lg"
        >
          Sign in
        </Link>
      </>
    );
  }

  return (
    <AuthBody>
      <AuthHeading title="Email change" />
      <div className="text-center">
        {state === "working" ? (
          <p className="flex items-center justify-center gap-2 text-sm text-[var(--rago-text-2)]">
            <Spinner /> Confirming your new email…
          </p>
        ) : state === "done" ? (
          <>
            <SuccessNote>
              Your new email is confirmed. Use it the next time you sign in.
            </SuccessNote>
            {session.status === "signedIn" ? (
              <Link to="/" className="rago-btn rago-btn-block rago-btn-lg">
                Continue to Rago
              </Link>
            ) : (
              <Link
                to="/auth/signin"
                search={{ next: undefined }}
                className="rago-btn rago-btn-block rago-btn-lg"
              >
                Sign in
              </Link>
            )}
          </>
        ) : state === "missing" || !token ? (
          <p className="text-sm text-[var(--rago-text-2)]">
            This page needs the link from your confirmation email.
          </p>
        ) : (
          <>
            <ErrorNote error={error}>{describeAccountError(error, "emailConfirm").message}</ErrorNote>
            {isLinkVerdict(error) ? (
              startAgain()
            ) : (
              // Not the link's fault (the network, a rate limit): the same
              // link is still good.
              <button
                type="button"
                className="rago-btn rago-btn-block rago-btn-lg"
                onClick={() => confirm(token)}
              >
                Try again
              </button>
            )}
          </>
        )}
      </div>
    </AuthBody>
  );
}
